<template>
    <DModal
        v-model="terminateForm.activity_log_comment"
        :title="__('customers.terminations.termination_title')"
        is-confirm
        :show-change-reason-input="true"
        is-error
        :show="showModal"
        :is-awaiting-validation="true"
        :show-incident-list="true"
        :customer-id="contract?.customer_id ?? 0"
        @setIncident="setIncident"
        @confirm="terminateConfirm"
        @close="$emit('close-modal')"
        @clearFields="resetTerminationForm()"
    >
        <div style="padding: 20px;">
            <DRow style="margin-bottom: 24px;">
                <DCol style="display: flex; align-items: center; gap: 1rem;">
                    <DSwitch
                        v-model="showTerminationInputField"
                        color="success"
                        variant="3d"
                        @update:checked="resetTerminationData()"
                    />
                    <label style="margin-bottom: 0;">{{ __('customers.terminations.free_input_of_termination_date') }}</label>
                </DCol>
            </DRow>
            <DRow style="margin-top: 16px;">
                <DCol>
                    <DDateFormatInput
                        id="entry_date"
                        v-model="terminateForm.entry_date"
                        :label="__('customers.terminations.termination_entry_date')"
                        format="YYYY-MM-DD"
                        add-label-classes="required"
                        type="date"
                        @update:modelValue="fetchOptions"
                    />
                    <p
                        v-if="errors.entry_date"
                        class="help-block text-danger"
                    >
                        {{ errors.entry_date }}
                    </p>
                </DCol>
            </DRow>
            <DRow style="margin-top: 16px;">
                <DCol v-show="!showTerminationInputField && terminateForm.terminate_immediately === false">
                    <DSelect
                        id="date"
                        v-model="selectedTerminationDate"
                        :label="__('customers.terminations.termination_date')"
                        :placeholder="__('customers.terminations.select_termination_date')"
                        add-label-classes="required"
                        :options="terminationDates"
                        @update:value="setTerminationData"
                    />
                    <p
                        v-if="errors.date"
                        class="help-block text-danger"
                    >
                        {{ errors.date }}
                    </p>
                </DCol>
                <DCol v-show="showTerminationInputField && terminateForm.terminate_immediately === false">
                    <DDateFormatInput
                        id="termination_date"
                        v-model="terminateForm.date"
                        :label="__('customers.terminations.termination_date')"
                        format="YYYY-MM-DD"
                        add-label-classes="required"
                        type="date"
                        @update:modelValue="resetExtraordinaryFlag"
                    />
                    <p
                        v-if="errors.date"
                        class="help-block text-danger"
                    >
                        {{ errors.date }}
                    </p>
                </DCol>
            </DRow>
            <DRow style="margin-top: 16px;">
                <DCol>
                    <DSelect
                        id="short_name"
                        v-model="terminateForm.short_name"
                        :label="__('customers.terminations.termination_reason')"
                        add-label-classes="required"
                        :options="terminationReasons"
                        :placeholder="__('customers.terminations.select_termination_reason')"
                    />
                    <p
                        v-if="errors.short_name"
                        class="help-block text-danger"
                    >
                        {{ errors.short_name }}
                    </p>
                </DCol>
            </DRow>
            <DRow
                style="margin-top: 16px;"
            >
                <DCol sm="12">
                    <label class="required">{{ __('customers.terminations.send_termination_email') }}</label>
                </DCol>
                <DCol sm="12">
                    <DSwitch
                        v-model="terminateForm.send_termination_email"
                        color="success"
                        variant="3d"
                    />
                    <p
                        v-if="errors.send_termination_email"
                        class="help-block text-danger mt-1"
                    >
                        {{ errors.send_termination_email }}
                    </p>
                </DCol>
            </DRow>
            <DRow
                v-if="userPermissions.includes('contract.terminate_immediately')"
                style="margin-top: 16px;"
            >
                <DCol sm="12">
                    <label class="required">{{ __('customers.terminations.terminate_immediately') }}</label>
                </DCol>
                <DCol sm="12">
                    <DSwitch
                        v-model="terminateForm.terminate_immediately"
                        color="success"
                        variant="3d"
                        @update:checked="resetExtraordinaryFlag"
                    />
                    <p
                        v-if="errors.terminate_immediately"
                        class="help-block text-danger mt-1"
                    >
                        {{ errors.terminate_immediately }}
                    </p>
                </DCol>
            </DRow>
            <DRow
                v-if="contract && contract.fulfillment_account_id !== null && contract.supports_send_auth_code === true"
                style="margin-top: 16px;"
            >
                <DCol sm="12">
                    <label class="required">{{ __('customers.terminations.send_auth_code') }}</label>
                </DCol>
                <DCol sm="12">
                    <DSwitch
                        v-model="terminateForm.send_auth_code"
                        color="success"
                        variant="3d"
                    />
                    <p
                        v-if="errors.send_auth_code"
                        class="help-block text-danger mt-1"
                    >
                        {{ errors.send_auth_code }}
                    </p>
                </DCol>
            </DRow>
            <DRow
                v-show="showTerminationInputField && !contract.is_consumer_protected"
                style="margin-top: 16px;"
            >
                <DCol sm="3">
                    <label>{{ __('customers.terminations.adjust_invoice') }}</label>
                </DCol>
                <DCol sm="5">
                    <DFormCheck
                        id="adjust_invoice"
                        v-model="terminateForm.adjust_invoice"
                        v-c-tooltip="__('customers.terminations.adjust_invoice_description')"
                        add-label-classes="required"
                    />
                    <p
                        v-if="errors.adjust_invoice"
                        class="help-block text-danger"
                    >
                        {{ errors.adjust_invoice }}
                    </p>
                </DCol>
            </DRow>
            <DRow
                v-show="contract.hasAddons"
                class="pt-3 pb-0"
            >
                <DAlert
                    color="warning"
                    class="mb-0"
                >
                    {{ __('customers.terminations.contract_has_addons') }}
                </DAlert>
            </DRow>
        </div>
    </DModal>
</template>

<script>

import { parseInt } from 'lodash'

export default {
    name: 'TerminateContractModal',
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        terminationReasons: {
            type: Array,
            default: () => { return [] }
        },
        terminationDates: {
            type: Array,
            default: () => { return [] }
        },
        errorsData: {
            type: Object,
            default: () => { return {} }
        },
        redirectTo: {
            type: String,
            default: 'contract-list'
        },
        filters: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['close-modal', 'update-termination-dates'],
    data () {
        return {
            terminateForm: this.$inertia.form({
                date: null,
                short_name: null,
                entry_date: new Date().toISOString().split('T')[0],
                activity_log_comment: '',
                adjust_invoice: this.contract?.is_consumer_protected ?? false,
                send_auth_code: false,
                send_termination_email: true,
                incident_id: null,
                terminate_immediately: false,
                extraordinary: false
            }),
            showTerminationInputField: false,
            errors: this.errorsData,
            selectedTerminationDate: ''
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    watch: {
        contract: {
            handler: function () {
                this.terminateForm.adjust_invoice = this.contract?.is_consumer_protected ?? false
            },
            deep: true
        }
    },
    methods: {
        terminateConfirm () {
            this.terminateForm.delete(this.$route('customers.customers.contracts.destroy', {
                customer: this.contract.customer_id,
                contract: this.contract.id,
                redirectTo: this.redirectTo,
                filters: this.filters
            }), {
                onSuccess: () => {
                    this.$emit('close-modal')
                    this.resetTerminationForm()
                },
                onError: (error) => {
                    this.errors = error
                }
            })
        },
        resetTerminationForm () {
            this.selectedTerminationDate = ''
            this.terminateForm.date = null
            this.terminateForm.entry_date = new Date().toISOString().split('T')[0]
            this.terminateForm.short_name = null
            this.terminateForm.extraordinary = false
            this.errors = {}
        },
        fetchOptions (entryDate) {
            window.axios.get(this.$route('contracts.termination_dates', { contract: this.contract.id, entry_date: entryDate }))
                .then(response => {
                    this.$emit('update-termination-dates', response.data)
                })
        },
        setIncident (value) {
            this.terminateForm.incident_id = value
        },
        setTerminationData (value) {
            const selectedData = this.terminationDates[parseInt(value) - 1] ?? null // Need to add -1 because on BE we add +1, key 0 is also NULL, and then we can have default preselection
            if (selectedData === null) {
                return
            }

            this.terminateForm.date = selectedData.date
            this.terminateForm.extraordinary = selectedData.extraordinary
        },
        resetExtraordinaryFlag () {
            this.terminateForm.extraordinary = false
        },
        resetTerminationData () {
            this.terminateForm.date = null
            this.terminateForm.extraordinary = false
            this.selectedTerminationDate = ''
        }
    }
}
</script>
